import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Plus,
  DollarSign,
  Loader2,
  Trash2,
  TrendingUp,
  Target,
  Calendar,
  User,
  Filter,
  BarChart3,
} from 'lucide-react';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm-provider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PipelineStage {
  id: string;
  name: string;
  order: number;
  color: string;
}

interface Pipeline {
  _id: string;
  name: string;
  stages: PipelineStage[];
  isDefault: boolean;
  createdAt: string;
}

interface Deal {
  _id: string;
  contactId: string;
  pipelineId: string;
  title: string;
  value: number; // cents
  currency: string;
  stage: string; // stage ID
  status: 'open' | 'won' | 'lost';
  probability: number;
  expectedCloseDate?: string;
  actualCloseDate?: string;
  assignedTo?: string;
  notes?: string;
  lostReason?: string;
  createdAt: string;
  updatedAt: string;
}

interface StageAnalytics {
  stageId: string;
  stageName: string;
  dealCount: number;
  totalValue: number;
}

interface PipelineAnalytics {
  pipelineId: string;
  pipelineName: string;
  stages: StageAnalytics[];
  totalDeals: number;
  totalValue: number;
  wonDeals: number;
  wonValue: number;
  lostDeals: number;
  conversionRate: number;
}

interface CRMContact {
  _id: string;
  email: string;
  name?: string;
}

interface DealFormState {
  title: string;
  contactId: string;
  stage: string;
  value: string; // major units as string for input
  currency: string;
  probability: string;
  expectedCloseDate: string;
  status: 'open' | 'won' | 'lost';
  lostReason: string;
  notes: string;
}

const EMPTY_FORM: DealFormState = {
  title: '',
  contactId: '',
  stage: '',
  value: '',
  currency: 'USD',
  probability: '50',
  expectedCloseDate: '',
  status: 'open',
  lostReason: '',
  notes: '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CRMPipeline() {
  const { projectId } = useParams<{ projectId: string }>();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [form, setForm] = useState<DealFormState>(EMPTY_FORM);
  const [search, setSearch] = useState('');

  const basePath = `/projects/${projectId}/crm`;

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  const { data: pipelinesData, isLoading: pipelinesLoading } = useQuery({
    queryKey: ['crm-pipelines', projectId],
    queryFn: async () => {
      const res = await api.get(`${basePath}/pipelines`);
      return res.data as { success: boolean; data: Pipeline[] };
    },
    enabled: Boolean(projectId),
  });

  const pipelines = pipelinesData?.data ?? [];
  const activePipelineId = selectedPipelineId || pipelines[0]?._id || '';
  const activePipeline = pipelines.find((p) => p._id === activePipelineId);

  const { data: analyticsData } = useQuery({
    queryKey: ['crm-analytics', projectId, activePipelineId],
    queryFn: async () => {
      const res = await api.get(`${basePath}/pipelines/${activePipelineId}/analytics`);
      return res.data as { success: boolean; data: PipelineAnalytics };
    },
    enabled: Boolean(projectId) && Boolean(activePipelineId),
  });

  const analytics = analyticsData?.data;

  const { data: dealsData, isLoading: dealsLoading } = useQuery({
    queryKey: ['crm-deals', projectId, activePipelineId, search],
    queryFn: async () => {
      const res = await api.get(`${basePath}/deals`, {
        params: {
          pipelineId: activePipelineId,
          search: search || undefined,
          limit: 500,
        },
      });
      return res.data as { success: boolean; data: Deal[]; meta: unknown };
    },
    enabled: Boolean(projectId) && Boolean(activePipelineId),
  });

  const deals = dealsData?.data ?? [];

  const { data: contactsData } = useQuery({
    queryKey: ['crm-contacts-list', projectId],
    queryFn: async () => {
      const res = await api.get(`${basePath}/contacts`, { params: { limit: 100 } });
      return res.data as { success: boolean; data: CRMContact[] };
    },
    enabled: Boolean(projectId),
  });

  const contacts = contactsData?.data ?? [];

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const createDeal = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await api.post(`${basePath}/deals`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-deals', projectId] });
      queryClient.invalidateQueries({ queryKey: ['crm-analytics', projectId] });
      toast.success('Deal created');
      closeDialog();
    },
    onError: () => toast.error('Failed to create deal'),
  });

  const updateDeal = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await api.put(`${basePath}/deals/${id}`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-deals', projectId] });
      queryClient.invalidateQueries({ queryKey: ['crm-analytics', projectId] });
      toast.success('Deal updated');
      closeDialog();
    },
    onError: () => toast.error('Failed to update deal'),
  });

  const deleteDeal = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`${basePath}/deals/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-deals', projectId] });
      queryClient.invalidateQueries({ queryKey: ['crm-analytics', projectId] });
      toast.success('Deal deleted');
      closeDialog();
    },
    onError: () => toast.error('Failed to delete deal'),
  });

  // -------------------------------------------------------------------------
  // Dialog helpers
  // -------------------------------------------------------------------------

  function openCreateDialog(stageId: string) {
    setEditingDeal(null);
    setForm({ ...EMPTY_FORM, stage: stageId });
    setDialogOpen(true);
  }

  function openEditDialog(deal: Deal) {
    setEditingDeal(deal);
    setForm({
      title: deal.title,
      contactId: deal.contactId,
      stage: deal.stage,
      value: String(deal.value / 100),
      currency: deal.currency || 'USD',
      probability: String(deal.probability ?? 50),
      expectedCloseDate: deal.expectedCloseDate
        ? new Date(deal.expectedCloseDate).toISOString().split('T')[0]
        : '',
      status: deal.status,
      lostReason: deal.lostReason || '',
      notes: deal.notes || '',
    });
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingDeal(null);
    setForm(EMPTY_FORM);
  }

  function handleSave() {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.contactId) {
      toast.error('Contact is required');
      return;
    }

    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      contactId: form.contactId,
      pipelineId: activePipelineId,
      stage: form.stage,
      value: Math.round(parseFloat(form.value || '0') * 100),
      currency: form.currency || 'USD',
      probability: parseInt(form.probability || '50', 10),
      expectedCloseDate: form.expectedCloseDate || undefined,
      notes: form.notes || undefined,
    };

    if (editingDeal) {
      payload.status = form.status;
      if (form.status === 'lost') payload.lostReason = form.lostReason;
      updateDeal.mutate({ id: editingDeal._id, data: payload });
    } else {
      createDeal.mutate(payload);
    }
  }

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const stages = activePipeline?.stages.slice().sort((a, b) => a.order - b.order) ?? [];

  function dealsForStage(stageId: string) {
    return deals.filter((d) => d.stage === stageId);
  }

  function stageAnalytics(stageId: string): StageAnalytics | undefined {
    return analytics?.stages.find((s) => s.stageId === stageId);
  }

  function contactName(contactId: string): string {
    const c = contacts.find((ct) => ct._id === contactId);
    return c?.name || c?.email || 'Contact';
  }

  const isSaving = createDeal.isPending || updateDeal.isPending;
  const isLoading = pipelinesLoading || dealsLoading;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/projects/${projectId}`}
          className="hover:bg-muted rounded-lg p-2 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Sales Pipeline</h1>
          <p className="text-muted-foreground text-sm">Track and manage your deals</p>
        </div>
        <Link to={`/projects/${projectId}/crm/settings`}>
          <Button variant="outline" size="sm">
            <BarChart3 className="mr-2 h-4 w-4" />
            CRM Settings
          </Button>
        </Link>
      </div>

      {/* Pipeline selector + search */}
      <div className="flex flex-wrap items-center gap-3">
        {pipelines.length > 1 && (
          <Select value={activePipelineId} onValueChange={setSelectedPipelineId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select pipeline" />
            </SelectTrigger>
            <SelectContent>
              {pipelines.map((p) => (
                <SelectItem key={p._id} value={p._id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="relative">
          <Filter className="text-muted-foreground absolute left-2.5 top-2.5 h-4 w-4" />
          <Input
            placeholder="Search deals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-[240px] pl-9"
          />
        </div>
      </div>

      {/* Summary bar */}
      {analytics && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
              <Target className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.totalDeals}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Value</CardTitle>
              <DollarSign className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency(analytics.totalValue)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Won Deals</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.wonDeals}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
              <BarChart3 className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.conversionRate.toFixed(1)}%</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Kanban board */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </div>
      ) : stages.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Target className="text-muted-foreground mb-4 h-12 w-12" />
            <h3 className="text-lg font-semibold">No pipeline found</h3>
            <p className="text-muted-foreground mt-1">
              Create a pipeline in CRM Settings to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((stage) => {
            const stageDeals = dealsForStage(stage.id);
            const sa = stageAnalytics(stage.id);
            return (
              <div
                key={stage.id}
                className="bg-card flex min-w-[280px] flex-shrink-0 flex-col rounded-lg border"
              >
                {/* Column header */}
                <div
                  className="flex items-center justify-between rounded-t-lg border-b px-4 py-3"
                  style={{ borderTopColor: stage.color, borderTopWidth: 3 }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: stage.color }}
                    />
                    <h3 className="text-sm font-semibold">{stage.name}</h3>
                    <Badge variant="secondary" className="text-xs">
                      {stageDeals.length}
                    </Badge>
                  </div>
                  {sa && (
                    <span className="text-muted-foreground text-xs font-medium">
                      {formatCurrency(sa.totalValue)}
                    </span>
                  )}
                </div>

                {/* Deal cards */}
                <div className="flex flex-1 flex-col gap-2 p-2">
                  {stageDeals.map((deal) => (
                    <button
                      key={deal._id}
                      type="button"
                      onClick={() => openEditDialog(deal)}
                      className={`w-full rounded-md border p-3 text-left shadow-sm transition-shadow hover:shadow-md ${
                        deal.status === 'won'
                          ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950'
                          : deal.status === 'lost'
                            ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950'
                            : 'bg-background'
                      }`}
                    >
                      <div className="mb-1 font-medium leading-snug">{deal.title}</div>
                      <div className="text-muted-foreground flex items-center gap-1 text-xs">
                        <DollarSign className="h-3 w-3" />
                        {formatCurrency(deal.value, deal.currency)}
                      </div>
                      <div className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
                        <User className="h-3 w-3" />
                        {contactName(deal.contactId)}
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-muted-foreground text-xs">
                          {deal.probability}% probability
                        </span>
                        {deal.status !== 'open' && (
                          <Badge
                            variant={deal.status === 'won' ? 'default' : 'destructive'}
                            className="px-1.5 py-0 text-[10px]"
                          >
                            {deal.status === 'won' ? 'Won' : 'Lost'}
                          </Badge>
                        )}
                      </div>
                      {deal.expectedCloseDate && (
                        <div className="text-muted-foreground mt-1 flex items-center gap-1 text-xs">
                          <Calendar className="h-3 w-3" />
                          {formatDate(deal.expectedCloseDate)}
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {/* Add deal button */}
                <div className="border-t p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => openCreateDialog(stage.id)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Deal
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Deal Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingDeal ? 'Edit Deal' : 'New Deal'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            {/* Title */}
            <div className="grid gap-1.5">
              <Label htmlFor="deal-title">Title *</Label>
              <Input
                id="deal-title"
                placeholder="Deal title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>

            {/* Contact */}
            <div className="grid gap-1.5">
              <Label>Contact *</Label>
              <Select
                value={form.contactId}
                onValueChange={(v) => setForm((f) => ({ ...f, contactId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a contact" />
                </SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.name || c.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stage */}
            <div className="grid gap-1.5">
              <Label>Stage</Label>
              <Select
                value={form.stage}
                onValueChange={(v) => setForm((f) => ({ ...f, stage: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: s.color }}
                        />
                        {s.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Value + Currency */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 grid gap-1.5">
                <Label htmlFor="deal-value">Value</Label>
                <Input
                  id="deal-value"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.value}
                  onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="deal-currency">Currency</Label>
                <Input
                  id="deal-currency"
                  placeholder="USD"
                  value={form.currency}
                  onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                />
              </div>
            </div>

            {/* Probability */}
            <div className="grid gap-1.5">
              <Label htmlFor="deal-prob">Probability (%)</Label>
              <Input
                id="deal-prob"
                type="number"
                min="0"
                max="100"
                value={form.probability}
                onChange={(e) => setForm((f) => ({ ...f, probability: e.target.value }))}
              />
            </div>

            {/* Expected Close Date */}
            <div className="grid gap-1.5">
              <Label htmlFor="deal-close">Expected Close Date</Label>
              <Input
                id="deal-close"
                type="date"
                value={form.expectedCloseDate}
                onChange={(e) => setForm((f) => ({ ...f, expectedCloseDate: e.target.value }))}
              />
            </div>

            {/* Status (edit only) */}
            {editingDeal && (
              <div className="grid gap-1.5">
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, status: v as 'open' | 'won' | 'lost' }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Lost Reason */}
            {form.status === 'lost' && (
              <div className="grid gap-1.5">
                <Label htmlFor="deal-lost">Lost Reason</Label>
                <textarea
                  id="deal-lost"
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[60px] w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  rows={2}
                  value={form.lostReason}
                  onChange={(e) => setForm((f) => ({ ...f, lostReason: e.target.value }))}
                />
              </div>
            )}

            {/* Notes */}
            <div className="grid gap-1.5">
              <Label htmlFor="deal-notes">Notes</Label>
              <textarea
                id="deal-notes"
                className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[60px] w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                rows={3}
                placeholder="Any additional notes..."
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            {editingDeal && (
              <Button
                variant="destructive"
                size="sm"
                disabled={deleteDeal.isPending}
                onClick={async () => {
                  if (
                    await confirm({
                      title: 'Delete deal?',
                      message: 'Are you sure you want to delete this deal?',
                      danger: true,
                    })
                  ) {
                    deleteDeal.mutate(editingDeal._id);
                  }
                }}
              >
                {deleteDeal.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Delete
              </Button>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingDeal ? 'Update Deal' : 'Create Deal'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
